import { create } from 'zustand';
import {
  DocumentData,
  FormationItemData,
  PDFAssetData,
  PEWorkData,
  ProjectElementData,
  ProjectElementDataWithDetail,
  ProjectElementDataWithDocument,
  ProjectElementDataWithCarousel,
  ProjectElementDataWithFormation,
  ProjectElementDataWithPDF,
  ProjectElementDataWithText,
  ProjectElementDataWithTextBox,
  ProjectElementDataWithWork,
  SelectedData,
  TextBlockData,
  TextBlockPatch,
  TextBoxData,
  TextData,
  TextSourceData,
  WorkDetailData,
  WorkDetailSimpleData,
  WorkSimpleWithDetails,
} from '../dto/EntityRepository';
import {
  DetailType,
  DisplayAlignment,
  DisplaySize,
  FormationHorizontalAlignment,
  FormationImageMode,
  FormationType,
  FormationVerticalAlignment,
  normalizePEInfoType,
  PEInfoType,
  ProjectElementType,
} from '../enum/EnumRepository';
import {
  adjustFormationItemsForType,
  buildFormationSlotWorkDetailSnapshot,
  buildFormationSlotWorkSnapshot,
  getFormationHorizontalAlignment,
  getFormationImageMode,
  getFormationItemSource,
  getFormationVerticalAlignment,
  normalizeFormationData,
  normalizeFormationType,
} from '../util/formationUtils';
import {
  applyCarouselDirectUpload,
  applyCarouselImports,
  applyCarouselItemOrder,
  type CarouselImportSelection,
  getCarouselItemSource,
  normalizeCarouselData,
  removeCarouselItemsByIds,
  removeCarouselSlot,
  replaceCarouselSlot,
} from '../util/carouselUtils';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

function normalizeProjectElement(pe: ProjectElementData): ProjectElementData {
  switch (pe.projectElementType) {
    case ProjectElementType.TEXTBOX:
      return {
        ...pe,
        displayAlignment: pe.displayAlignment ?? DisplayAlignment.CENTER,
        displaySize: pe.displaySize ?? DisplaySize.BIG,
      };
    case ProjectElementType.WORK:
    case ProjectElementType.DETAIL:
      return {
        ...pe,
        infoType: normalizePEInfoType(pe.infoType),
      };
    case ProjectElementType.DOCUMENT:
      return {
        ...pe,
        infoType: normalizePEInfoType(pe.infoType),
        document: {
          ...pe.document,
          detailType: pe.document.detailType ?? DetailType.IMAGE,
          videoUrl: pe.document.videoUrl ?? '',
        },
      };
    case ProjectElementType.FORMATION:
      return {
        ...pe,
        infoType: normalizePEInfoType(pe.infoType),
        formation: normalizeFormationData(pe.formation),
      } satisfies ProjectElementDataWithFormation;
    case ProjectElementType.CAROUSEL:
      return {
        ...pe,
        displayAlignment: DisplayAlignment.CENTER,
        displaySize: pe.displaySize ?? DisplaySize.BIG,
        infoType: normalizePEInfoType(pe.infoType),
        carousel: normalizeCarouselData(pe.carousel),
      } satisfies ProjectElementDataWithCarousel;
    default:
      return pe;
  }
}

interface ProjectElementListState {
  projectElementList: ProjectElementData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  listProjectId: string | null;
  status: ResourceStatus;
  setProjectElementList: (projectElementList: ProjectElementData[]) => void;
  beginProjectElementListFetch: (ownerAui: string, scope: ResourceScope, projectId: string) => void;
  setProjectElementListWithScope: (
    projectElementList: ProjectElementData[],
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => void;
  setProjectElementListError: () => void;
  addProjectElementList: (projectElementList: ProjectElementData[]) => void;
  updateWork: (id: string, updates: Partial<PEWorkData>) => void;
  updateDetail: (id: string, updates: Partial<WorkDetailData>) => void;
  updateDocument: (id: string, updates: Partial<DocumentData>) => void;
  updatePDF: (id: string, updates: Partial<PDFAssetData>) => void;
  updatePDFFile: (id: string, originUrl: string, fileSize: number) => void;
  updateTextBox: (id: string, updates: Partial<TextBoxData>) => void;
  updateText: (peId: string, updates: Partial<TextData> & { source?: Partial<TextSourceData> }) => void;
  updateTextBlock: (peId: string, textBlockId: string, updates: TextBlockPatch) => void;
  updateImage: (id: string, originUrl: string, width: number, height: number) => void;
  updateDisplayAlignment: (id: string, displayAlignment: DisplayAlignment) => void;
  updateDisplaySize: (id: string, displaySize: DisplaySize) => void;
  updateInfoType: (id: string, infoType: PEInfoType) => void;
  updateFormationType: (id: string, formationType: FormationType) => void;
  updateFormationSlotImage: (
    peId: string,
    slotIndex: number,
    originUrl: string,
    width: number,
    height: number,
    caption: string,
  ) => void;
  updateFormationSlotCaption: (
    peId: string,
    slotIndex: number,
    caption: string,
  ) => void;
  updateFormationSlotFocus: (
    peId: string,
    slotIndex: number,
    focusX: number,
    focusY: number,
  ) => void;
  updateFormationSlotImageMode: (
    peId: string,
    slotIndex: number,
    imageMode: FormationImageMode,
  ) => void;
  updateFormationSlotAlignment: (
    peId: string,
    slotIndex: number,
    horizontalAlignment: FormationHorizontalAlignment,
    verticalAlignment: FormationVerticalAlignment,
  ) => void;
  updateFormationSlotReference: (
    peId: string,
    slotIndex: number,
    selected: SelectedData,
    work?: WorkSimpleWithDetails,
    detail?: WorkDetailSimpleData,
  ) => void;
  updateFormationSlotReferences: (
    peId: string,
    assignments: FormationSlotReferenceAssignment[],
  ) => void;
  updateCarouselSlotImage: (
    peId: string,
    slotIndex: number,
    originUrl: string,
    width: number,
    height: number,
    caption: string,
  ) => void;
  updateCarouselSlotCaption: (peId: string, slotIndex: number, caption: string) => void;
  updateCarouselSlotReference: (
    peId: string,
    slotIndex: number,
    selected: SelectedData,
    work?: WorkSimpleWithDetails,
    detail?: WorkDetailSimpleData,
  ) => void;
  importCarouselSelections: (peId: string, selections: CarouselImportSelection[]) => number;
  removeCarouselSlot: (peId: string, slotIndex: number) => void;
  removeCarouselItemsByIds: (peId: string, ids: string[]) => void;
  reorderCarouselItems: (peId: string, orderedIds: string[]) => void;
  afterCreateProjectElement: (projectElement: ProjectElementData) => void;
  afterUpdateProjectElement: (updatedProjectElement: ProjectElementData) => void;
  afterUpdateProjectElementBulkInfo: (updatedProjectElements: ProjectElementData[]) => void;
  afterDeleteProjectElement: (id: string) => void;
  clearProjectElementList: () => void;
  
  afterAddTextBlockInPE: (projectElement: ProjectElementData) => void;
  afterDeleteTextBlockInPE: (peId: string, textBlockId: string) => void;
}

export interface FormationSlotReferenceAssignment {
  slotIndex: number;
  selected: SelectedData;
  work?: WorkSimpleWithDetails;
  detail?: WorkDetailSimpleData;
  replaceExisting?: boolean;
}

const applyFormationSlotReference = (
  item: FormationItemData,
  selected: SelectedData,
  work?: WorkSimpleWithDetails,
  detail?: WorkDetailSimpleData,
): FormationItemData => {
  const common = {
    ...item,
    uploadFile: {
      id: '',
      originUrl: '',
      allowDownload: false,
      width: 0,
      height: 0,
    },
    caption: '',
    focusX: 0.5,
    focusY: 0.5,
    imageMode: FormationImageMode.FIT,
    horizontalAlignment: FormationHorizontalAlignment.CENTER,
    verticalAlignment: FormationVerticalAlignment.CENTER,
    referenceChanged: true,
    imageChanged: false,
    captionChanged: false,
    focusChanged: false,
    presentationChanged: false,
  };

  if (selected.detailId) {
    return {
      ...common,
      work: null,
      workDetail: buildFormationSlotWorkDetailSnapshot(selected, detail),
    };
  }

  return {
    ...common,
    work: buildFormationSlotWorkSnapshot(selected, work),
    workDetail: null,
  };
};

export const useProjectElementListStore = create<ProjectElementListState>((set) => ({
  projectElementList: [],
  listOwnerAui: null,
  listScope: null,
  listProjectId: null,
  status: 'idle',
  addProjectElementList: (newItems) =>
    set(({ projectElementList }) => ({
      projectElementList: [
        ...projectElementList,
        ...newItems.map((pe) => {
          const normalized = normalizeProjectElement(pe);
          return {
            ...normalized,
            hasChanged: false,
            imageChanged: false,
          };
        }),
      ],
    })),
  setProjectElementList: (projectElementList) =>
    set(() => ({
      projectElementList: projectElementList.map((pe) => {
        const normalized = normalizeProjectElement(pe);
        return {
          ...normalized,
          hasChanged: false,
          imageChanged: false,
        };
      }),
    })),
  beginProjectElementListFetch: (ownerAui, scope, projectId) => set((state) => {
    const normalizedId = String(projectId);
    // 같은 owner+scope+projectId 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.listProjectId === normalizedId &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setProjectElementListWithScope: (projectElementList, ownerAui, scope, projectId) =>
    set({
      projectElementList: projectElementList.map((pe) => {
        const normalized = normalizeProjectElement(pe);
        return {
          ...normalized,
          hasChanged: false,
          imageChanged: false,
        };
      }),
      listOwnerAui: ownerAui,
      listScope: scope,
      listProjectId: String(projectId),
      status: 'success',
    }),
  setProjectElementListError: () => set({ status: 'error' }),
  updateWork: (id, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.WORK) return pe;

        return {
          ...pe,
          work: {
            ...pe.work,
            ...updates,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithWork;
      }),
    })),
  updateDetail: (id, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.DETAIL) return pe;
        return {
          ...pe,
          workDetail: {
            ...pe.workDetail,
            ...updates,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithDetail;
      }),
    })),
  updateDocument: (id, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.DOCUMENT) return pe;

        return {
          ...pe,
          document: {
            ...pe.document,
            ...updates,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithDocument;
      }),
    })),
  updatePDF: (id, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.PDF) return pe;

        return {
          ...pe,
          pdfAsset: {
            ...pe.pdfAsset,
            ...updates,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithPDF;
      }),
    })),
  updatePDFFile: (id, originUrl, fileSize) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.PDF) return pe;

        return {
          ...pe,
          pdfAsset: {
            ...pe.pdfAsset,
            uploadFile: {
              ...pe.pdfAsset.uploadFile,
              originUrl,
            },
            fileSize,
          },
          hasChanged: true,
          imageChanged: true,
        } satisfies ProjectElementDataWithPDF;
      }),
    })),
  updateTextBox: (id, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.TEXTBOX) return pe;

        return {
          ...pe,
          textBox: {
            ...pe.textBox,
            ...updates,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithTextBox;
      }),
    })),
  updateText: (peId, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.TEXT) return pe;

        const { source, ...textUpdates } = updates;

        return {
          ...pe,
          text: {
            ...pe.text,
            ...textUpdates,
            source: source
              ? {
                  ...pe.text.source,
                  ...source,
                }
              : pe.text.source,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithText;
      }),
    })),
  updateTextBlock: (peId, textBlockId, updates) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.TEXT) return pe;

        return {
          ...pe,
          selectedTextBlocks: pe.selectedTextBlocks.map((tb) =>
            tb.id === textBlockId
              ? { ...tb, ...updates, hasChanged: true } as TextBlockData
              : tb
          ),
          hasChanged: true,
        } satisfies ProjectElementDataWithText;
      }),
    })),
  updateImage: (id, originUrl, width, height) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;

        switch (pe.projectElementType) {
          case ProjectElementType.WORK:
            return {
              ...pe,
              work: {
                ...pe.work,
                uploadFile: {
                  ...pe.work.uploadFile,
                  originUrl,
                  width,
                  height,
                },
              },
              hasChanged: true,
              imageChanged: true,
            };

          case ProjectElementType.DETAIL:
            return {
              ...pe,
              workDetail: {
                ...pe.workDetail,
                uploadFile: {
                  ...pe.workDetail.uploadFile,
                  originUrl,
                  width,
                  height,
                },
              },
              hasChanged: true,
              imageChanged: true,
            };

          case ProjectElementType.DOCUMENT:
            return {
              ...pe,
              document: {
                ...pe.document,
                uploadFile: {
                  ...pe.document.uploadFile,
                  originUrl,
                  width,
                  height,
                },
              },
              hasChanged: true,
              imageChanged: true,
            };

          default:
            return pe;
        }
      }),
    })),
  updateDisplayAlignment: (id, displayAlignment) => set(({ projectElementList }) => ({
    projectElementList: projectElementList.map((pe) =>
      pe.id === id ?
        {
          ...pe,
          displayAlignment,
          hasChanged: true
        }
        : pe
    ),
  })),
  updateDisplaySize: (id, displaySize) => set(({ projectElementList }) => ({
    projectElementList: projectElementList.map((pe) =>
      pe.id === id ?
        {
          ...pe,
          displaySize,
          hasChanged: true
        }
        : pe
    ),
  })),
  updateInfoType: (id, infoType) => set(({ projectElementList }) => ({
    projectElementList: projectElementList.map((pe) => {
      if (pe.id !== id) return pe;
      if (
        pe.projectElementType !== ProjectElementType.WORK &&
        pe.projectElementType !== ProjectElementType.DETAIL &&
        pe.projectElementType !== ProjectElementType.DOCUMENT &&
        pe.projectElementType !== ProjectElementType.FORMATION &&
        pe.projectElementType !== ProjectElementType.CAROUSEL
      ) {
        return pe;
      }
      if (normalizePEInfoType(pe.infoType) === infoType) return pe;

      return {
        ...pe,
        infoType,
        hasChanged: true,
      };
    }),
  })),
  updateFormationType: (id, formationType) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== id) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;
        const normalizedType = normalizeFormationType(formationType);
        if (pe.formation.formationType === normalizedType) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            formationType: normalizedType,
            items: adjustFormationItemsForType(pe.formation.items, normalizedType),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotImage: (peId, slotIndex, originUrl, width, height, caption) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) =>
              item.slotIndex === slotIndex
                ? {
                    ...item,
                    work: null,
                    workDetail: null,
                    uploadFile: {
                      ...item.uploadFile,
                      originUrl,
                      width,
                      height,
                    },
                    caption,
                    focusX: 0.5,
                    focusY: 0.5,
                    imageMode: FormationImageMode.FIT,
                    horizontalAlignment: FormationHorizontalAlignment.CENTER,
                    verticalAlignment: FormationVerticalAlignment.CENTER,
                    imageChanged: true,
                    captionChanged: (item.caption ?? '') !== caption,
                    referenceChanged: false,
                    focusChanged: false,
                    presentationChanged: false,
                  }
                : item,
            ),
          },
          hasChanged: true,
          imageChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotCaption: (peId, slotIndex, caption) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;

        const targetItem = pe.formation.items.find((item) => item.slotIndex === slotIndex);
        if (
          !targetItem ||
          getFormationItemSource(targetItem) !== 'directUpload' ||
          (targetItem.caption ?? '') === caption
        ) {
          return pe;
        }

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) =>
              item.slotIndex === slotIndex
                ? { ...item, caption, captionChanged: true }
                : item,
            ),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotFocus: (peId, slotIndex, focusX, focusY) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) =>
              item.slotIndex === slotIndex
                ? { ...item, focusX, focusY, focusChanged: true }
                : item,
            ),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotImageMode: (peId, slotIndex, imageMode) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;
        const targetItem = pe.formation.items.find((item) => item.slotIndex === slotIndex);
        if (!targetItem || getFormationImageMode(targetItem) === imageMode) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) =>
              item.slotIndex === slotIndex
                ? { ...item, imageMode, presentationChanged: true }
                : item,
            ),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotAlignment: (
    peId,
    slotIndex,
    horizontalAlignment,
    verticalAlignment,
  ) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;
        const targetItem = pe.formation.items.find((item) => item.slotIndex === slotIndex);
        if (
          !targetItem ||
          (
            getFormationHorizontalAlignment(targetItem) === horizontalAlignment &&
            getFormationVerticalAlignment(targetItem) === verticalAlignment
          )
        ) {
          return pe;
        }

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) =>
              item.slotIndex === slotIndex
                ? {
                    ...item,
                    horizontalAlignment,
                    verticalAlignment,
                    presentationChanged: true,
                  }
                : item,
            ),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotReference: (peId, slotIndex, selected, work, detail) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) => (
              item.slotIndex === slotIndex
                ? applyFormationSlotReference(item, selected, work, detail)
                : item
            )),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateFormationSlotReferences: (peId, assignments) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.FORMATION) return pe;

        const assignmentBySlot = new Map(
          assignments.map((assignment) => [assignment.slotIndex, assignment]),
        );
        const hasApplicableAssignment = pe.formation.items.some((item) => (
          assignmentBySlot.get(item.slotIndex)?.replaceExisting
          || (
            assignmentBySlot.has(item.slotIndex)
            && getFormationItemSource(item) === 'empty'
          )
        ));
        if (!hasApplicableAssignment) return pe;

        return {
          ...pe,
          formation: {
            ...pe.formation,
            items: pe.formation.items.map((item) => {
              const assignment = assignmentBySlot.get(item.slotIndex);
              if (
                !assignment
                || (
                  getFormationItemSource(item) !== 'empty'
                  && !assignment.replaceExisting
                )
              ) return item;
              return applyFormationSlotReference(
                item,
                assignment.selected,
                assignment.work,
                assignment.detail,
              );
            }),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithFormation;
      }),
    })),
  updateCarouselSlotImage: (peId, slotIndex, originUrl, width, height, caption) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: pe.carousel.items.map((item) => (
              item.slotIndex === slotIndex
                ? applyCarouselDirectUpload(item, originUrl, width, height, caption)
                : item
            )),
          },
          hasChanged: true,
          imageChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  updateCarouselSlotCaption: (peId, slotIndex, caption) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        const targetItem = pe.carousel.items.find((item) => item.slotIndex === slotIndex);
        if (
          !targetItem
          || getCarouselItemSource(targetItem) !== 'directUpload'
          || (targetItem.caption ?? '') === caption
        ) {
          return pe;
        }
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: pe.carousel.items.map((item) => (
              item.slotIndex === slotIndex
                ? { ...item, caption, captionChanged: true }
                : item
            )),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  updateCarouselSlotReference: (peId, slotIndex, selected, work, detail) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: replaceCarouselSlot(pe.carousel.items, slotIndex, selected, work, detail),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  importCarouselSelections: (peId, selections) => {
    let omittedCount = 0;
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        const result = applyCarouselImports(pe.carousel.items, selections);
        omittedCount = result.omittedCount;
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: result.items,
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    }));
    return omittedCount;
  },
  removeCarouselItemsByIds: (peId, ids) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL || ids.length === 0) {
          return pe;
        }
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: removeCarouselItemsByIds(pe.carousel.items, ids),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  reorderCarouselItems: (peId, orderedIds) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: applyCarouselItemOrder(pe.carousel.items, orderedIds),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  removeCarouselSlot: (peId, slotIndex) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId || pe.projectElementType !== ProjectElementType.CAROUSEL) return pe;
        return {
          ...pe,
          carousel: {
            ...pe.carousel,
            items: removeCarouselSlot(pe.carousel.items, slotIndex),
          },
          hasChanged: true,
        } satisfies ProjectElementDataWithCarousel;
      }),
    })),
  afterCreateProjectElement: (projectElement) =>
    set(({ projectElementList }) => ({
      projectElementList: [
        ...projectElementList,
        { ...normalizeProjectElement(projectElement), hasChanged: false },
      ],
    })),
  afterUpdateProjectElement: (updatedPe) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) =>
        pe.id === updatedPe.id
          ? {
              ...normalizeProjectElement(updatedPe),
              hasChanged: false,
              imageChanged: false,
            }
          : pe,
      ),
    })),
  afterUpdateProjectElementBulkInfo: (updatedProjectElements) =>
    set((state) => {
      if (updatedProjectElements.length === 0) return state;
      const updatedById = new Map(updatedProjectElements.map((pe) => [pe.id, pe]));
      return {
        projectElementList: state.projectElementList.map((pe) => {
          const updated = updatedById.get(pe.id);
          if (!updated) return pe;
          return {
            ...normalizeProjectElement(updated),
            hasChanged: false,
            imageChanged: false,
          };
        }),
      };
    }),
  afterDeleteProjectElement: (id) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.filter((pe) => pe.id !== id)
    })),
  clearProjectElementList: () => set((state) => {
    if (
      state.projectElementList.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.listProjectId === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      projectElementList: [],
      listOwnerAui: null,
      listScope: null,
      listProjectId: null,
      status: 'idle',
    };
  }),
  afterAddTextBlockInPE: (newProjectElement: ProjectElementData) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== newProjectElement.id) return pe;
        if (pe.projectElementType !== ProjectElementType.TEXT) return pe;
        if (newProjectElement.projectElementType !== ProjectElementType.TEXT) return pe;

        const peWithText = pe as ProjectElementDataWithText;
        const newPeWithText = newProjectElement as ProjectElementDataWithText;

        // 기존 TextBlock들을 Map으로 만들어서 변경사항 보존
        const existingTextBlocksMap = new Map(
          peWithText.selectedTextBlocks.map((tb) => [tb.id, tb])
        );

        const mergedTextBlocks = newPeWithText.selectedTextBlocks.map((newTb) => {
          const existingTb = existingTextBlocksMap.get(newTb.id);
          if (existingTb) {
            return existingTb;
          }
          return newTb;
        });

        return {
          ...peWithText,
          selectedTextBlocks: mergedTextBlocks,
          hasChanged: peWithText.hasChanged ? peWithText.hasChanged : false,
        } satisfies ProjectElementDataWithText;
      }),
    })),
  afterDeleteTextBlockInPE: (peId, textBlockId) =>
    set(({ projectElementList }) => ({
      projectElementList: projectElementList.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.projectElementType !== ProjectElementType.TEXT) return pe;

        return {
          ...pe,
          selectedTextBlocks: pe.selectedTextBlocks.filter((tb) => tb.id !== textBlockId),
          hasChanged: pe.hasChanged ? pe.hasChanged : false,
        } satisfies ProjectElementDataWithText;
      }),
    })),
}));
